import React, { useLayoutEffect, useRef, useState } from 'react';

import { BANNER_COPY, PRIVACY_COOKIES_HREF } from '../../constants/cookieConsentCopy';
import { BOTTOM_CHROME_INSET_VARIABLE } from '../../contexts/BottomChromeContext';
import { useCookieConsent } from '../../contexts/CookieConsentContext';
import { useIsMobile } from '../../hooks/useIsMobile';
import { FILLS, FONTS, LAYERS, RADII, SHADOWS, STROKES } from '../../styles/tokens';

const PHONE_OFFSET_PX = 10;
const DESKTOP_OFFSET_PX = 14;
const BUTTON_MIN_WIDTH_PX = 84;

const buttonStyle: React.CSSProperties = {
  height: '34px',
  minWidth: `${BUTTON_MIN_WIDTH_PX}px`,
  padding: '0 14px',
  background: FILLS.input,
  border: `1px solid ${STROKES.elevated}`,
  borderRadius: RADII.pill,
  color: FILLS.textPrimary,
  fontFamily: FONTS.body,
  fontSize: '13px',
  fontWeight: 600,
  cursor: 'pointer',
};

const copyStyle: React.CSSProperties = {
  margin: 0,
  fontFamily: FONTS.body,
  fontSize: '13px',
  lineHeight: 1.45,
  color: FILLS.textLight,
};

const linkStyle: React.CSSProperties = {
  color: FILLS.goldWarm,
  textDecoration: 'underline',
};

/**
 * Cookie consent banner: a non-modal overlay fixed above the bottom chrome
 * (published as a CSS variable by the bottom-chrome provider). It is a region,
 * not a dialog, and the page beneath stays scrollable and clickable. An in-flow
 * spacer reserves its height so the last content can scroll clear of it.
 */
const ConsentBanner: React.FC = () => {
  const { isDecisionNeeded, isPreferencesOpen, acceptAnalytics, rejectAnalytics, openPreferences } =
    useCookieConsent();
  const isMobile = useIsMobile();
  const bannerRef = useRef<HTMLDivElement>(null);
  const [bannerHeight, setBannerHeight] = useState(0);
  const isVisible = isDecisionNeeded && !isPreferencesOpen;
  const offset = isMobile ? PHONE_OFFSET_PX : DESKTOP_OFFSET_PX;

  useLayoutEffect(() => {
    const banner = bannerRef.current;
    if (!isVisible || !banner) return undefined;
    const measure = () => setBannerHeight(banner.getBoundingClientRect().height);
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(banner);
    return () => observer.disconnect();
  }, [isVisible, isMobile]);

  if (!isVisible) return null;

  const bottom = `calc(var(${BOTTOM_CHROME_INSET_VARIABLE}, 0px) + ${offset}px)`;
  const positionStyle: React.CSSProperties = isMobile
    ? { left: '10px', right: '10px', borderRadius: RADII.card, padding: '12px' }
    : {
        left: '50%',
        transform: 'translateX(-50%)',
        width: 'max-content',
        maxWidth: 'calc(100% - 24px)',
        borderRadius: RADII.pill,
        padding: '8px 8px 8px 20px',
      };

  return (
    <>
      <div
        data-testid="consent-banner-spacer"
        aria-hidden="true"
        style={{ height: `${bannerHeight + offset}px` }}
      />
      <div
        ref={bannerRef}
        role="region"
        aria-label={BANNER_COPY.regionLabel}
        style={{
          position: 'fixed',
          bottom,
          zIndex: LAYERS.consentBanner,
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'stretch' : 'center',
          gap: isMobile ? '10px' : '16px',
          background: FILLS.surface,
          border: `1px solid ${STROKES.elevated}`,
          boxShadow: SHADOWS.consentBanner,
          boxSizing: 'border-box',
          ...positionStyle,
        }}
      >
        <p style={copyStyle}>
          {BANNER_COPY.question}{' '}
          <a href={PRIVACY_COOKIES_HREF} style={linkStyle}>
            {BANNER_COPY.policyLink}
          </a>
        </p>
        <div
          style={
            isMobile
              ? { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px' }
              : { display: 'flex', gap: '6px' }
          }
        >
          <button type="button" style={buttonStyle} onClick={rejectAnalytics}>
            {BANNER_COPY.reject}
          </button>
          <button type="button" style={buttonStyle} onClick={acceptAnalytics}>
            {BANNER_COPY.accept}
          </button>
          <button type="button" style={buttonStyle} onClick={openPreferences}>
            {BANNER_COPY.manage}
          </button>
        </div>
      </div>
    </>
  );
};

export default ConsentBanner;
